import type { LoaderFunctionArgs } from 'react-router'
import {
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from 'react-router'
import * as Sentry from '@sentry/react-router'
import { handleError } from '~/components/error-boundary'
import { BlogPostBanner, Byline } from '~/components'
import { BlogPostSidebar } from '~/components/blog/blog-post-sidebar'
import { blogCtaModuleLinks } from '~/components-v2/blog/blog-cta-module'
import postStyles from '~/styles/pages/page.css?url'
import pageStyles from '~/styles/pages/post.css?url'
import { z } from 'zod'
import { Breadcrumbs } from '~/components/blog/breadcrumbs'
import { CANONICAL_HOSTNAME } from '~/config'
import { blogPostRenderOption } from '~/utils/contentful'
import {
  getBlogPostBySlug,
  getBlogPosts,
} from '~/services/contentful/blog.server'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { styles } from '~/components-v2'
import { codeBlockLinks } from '~/components-v2/code-block'
import {
  PostCollection,
  postCollectionLinks,
} from '~/components-v2/post-collection/post-collection'

export const links = () => [
  { rel: 'stylesheet', href: postStyles },
  { rel: 'stylesheet', href: pageStyles },
  ...postCollectionLinks(),
  ...codeBlockLinks(),
  ...blogCtaModuleLinks(),
  { rel: 'stylesheet', href: styles.anchorReveal },
  { rel: 'stylesheet', href: styles.figure },
]

export { meta } from '~/utils'

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { slug } = z.object({ slug: z.string() }).parse(params)

  try {
    const post = await getBlogPostBySlug(slug)
    const [categoryId] = post.categoryIds ?? []
    const relatedPosts = await getBlogPosts({
      category: categoryId,
      limit: 7,
    })

    return {
      ...post,
      // Production host, not requestOrigin: a preview must not canonicalise
      // to itself and compete with the live post.
      canonical: `https://${CANONICAL_HOSTNAME}/blog/${slug}`,
      ogType: 'article' as const,
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: post.title, path: `/blog/${slug}` },
      ],
      latestPosts: relatedPosts.posts
        .filter((relatedPost) => relatedPost.slug !== slug)
        .slice(0, 6),
    }
  } catch {
    throw new Response('Post not found', { status: 404 })
  }
}

function Component() {
  const loaderData = useLoaderData<typeof loader>()
  const {
    body,
    title,
    subtitle,
    authors,
    banner,
    latestPosts,
    published,
    slug,
  } = loaderData

  return (
    <div className="blog-post">
      <header className="page">
        <Breadcrumbs
          className="post-breadcrumbs--inline"
          items={loaderData.breadcrumbs}
        />
        <h1 className="heading-xxl text-brand-dark">{title}</h1>
        {subtitle && <h2 className="caps-m text-primary">{subtitle}</h2>}
      </header>
      <Byline authors={authors} date={published} />
      <div className="blog-post-sidebar-column">
        <BlogPostSidebar
          authors={authors}
          breadcrumbs={loaderData.breadcrumbs}
          date={published}
          key={slug}
          postKey={slug}
          title={title}
        />
      </div>
      <article>
        {banner && <BlogPostBanner loading="eager" {...banner} />}
        {typeof body === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: body }} />
        ) : (
          documentToReactComponents(body, blogPostRenderOption)
        )}
      </article>
      {latestPosts.length > 0 && (
        <section className="blog-post-related">
          <PostCollection
            header={
              <h2 className="heading-xl text-brand-dark">
                Read more from Omni
              </h2>
            }
            posts={latestPosts}
            variant="carousel"
          />
        </section>
      )}
    </div>
  )
}

export default Component

export function ErrorBoundary() {
  const error = useRouteError()
  if (!isRouteErrorResponse(error)) Sentry.captureException(error)
  const { title, message } = handleError(error)

  return (
    <div className="blog-post">
      <header className="page">
        <h1 className="heading-xxl text-brand-dark">{title}</h1>
        {message && <h2 className="caps-m text-primary">{message}</h2>}
      </header>
    </div>
  )
}
